/**
 * Trade-opportunity map helpers (Mapbox HTML Marker–safe).
 * Mystery first — never put target card art on the pin.
 */

export function idStr(v) {
  if (v == null) return "";
  if (typeof v === "object" && v._id != null) return String(v._id);
  return String(v);
}

export function isWishlistMatchUser(user, matchedUserIds = []) {
  if (!user) return false;
  const set = new Set((matchedUserIds || []).map(idStr));
  return set.has(idStr(user.id)) || set.has(idStr(user._id));
}

export function getUserLngLat(user) {
  if (!user?.coordinates) return null;
  if (Array.isArray(user.coordinates) && user.coordinates.length >= 2) {
    const [lng, lat] = user.coordinates;
    if (typeof lng === "number" && typeof lat === "number" && !Number.isNaN(lng) && !Number.isNaN(lat)) {
      return { longitude: lng, latitude: lat };
    }
  }
  if (
    typeof user.coordinates.longitude === "number" &&
    typeof user.coordinates.latitude === "number"
  ) {
    return {
      longitude: user.coordinates.longitude,
      latitude: user.coordinates.latitude,
    };
  }
  return null;
}

/** Haversine-ish squared distance for sorting (good enough for priority). */
function dist2(a, b) {
  const dx = a.longitude - b.longitude;
  const dy = a.latitude - b.latitude;
  return dx * dx + dy * dy;
}

/**
 * Pick a single primary opportunity so only one callout is loud.
 * Prefers closest to viewer when viewerCoords provided.
 */
export function pickPrimaryOpportunity(users, matchedUserIds, viewerCoords = null) {
  const matches = (users || []).filter((u) => isWishlistMatchUser(u, matchedUserIds));
  const withCoords = matches.filter((u) => getUserLngLat(u));
  if (withCoords.length === 0) return null;
  if (!viewerCoords) return withCoords[0];
  const origin = {
    longitude: viewerCoords.longitude ?? viewerCoords.lng,
    latitude: viewerCoords.latitude ?? viewerCoords.lat,
  };
  if (typeof origin.longitude !== "number" || typeof origin.latitude !== "number") {
    return withCoords[0];
  }
  return [...withCoords].sort(
    (a, b) => dist2(getUserLngLat(a), origin) - dist2(getUserLngLat(b), origin)
  )[0];
}

/**
 * Copy never names the card — mystery / temptation only.
 *
 * `direction` used to be hardcoded to "they_have" at the call site because the
 * backend only computed that half. Now it comes from the match itself.
 */
export function opportunityCalloutCopy({ matchCount = 1, direction = "they_have" } = {}) {
  if (direction === "mutual") {
    // The strongest signal on the map: both sides already want something the
    // other has. Say so, because it needs no negotiation to be worth opening.
    return matchCount === 1
      ? "Perfect match — you each have something the other wants"
      : `Perfect match — ${matchCount} traders have a two-way swap with you`;
  }
  if (direction === "they_want") {
    // Tradable cards live in the Trade Binder, not the Vault (prized, not-for-
    // trade). Name it consistently with the reveal (#7A).
    return matchCount === 1
      ? "Trade opportunity — they want a card from your Trade Binder"
      : `Trade opportunity — ${matchCount} traders want cards from your Trade Binder`;
  }
  return matchCount === 1
    ? "Trade opportunity — they have something on your wishlist"
    : `Trade opportunity — ${matchCount} traders have cards you want`;
}

/** Match direction for a trader, defaulting safely for pre-change payloads. */
export function matchDirection(trader) {
  if (!trader) return "they_have";
  if (trader.direction) return trader.direction;
  const has = (trader.matchedCards || []).length > 0;
  const wants = (trader.theyWantCards || []).length > 0;
  if (has && wants) return "mutual";
  return wants ? "they_want" : "they_have";
}

/**
 * Banner copy for ONE trader's pin popup.
 *
 * Separate from `opportunityCalloutCopy` because the two surfaces are not the
 * same sentence: the map-wide callout speaks about a set ("3 traders want…")
 * and prefixes "Trade opportunity —", while a pin is exactly one collector and
 * already wears that label in its own header — repeating it there reads as a
 * stutter. The three directions and the mystery-first rule are shared: this
 * never names a card, and never says how many.
 *
 * The pin used to hardcode the forward sentence, so a collector whose Trade
 * Binder someone wanted — and anyone with a two-way match — was told the
 * opposite of what was true, on every pin (#P1-28).
 */
export function pinMatchBannerCopy(trader) {
  const direction = matchDirection(trader);
  if (direction === "mutual") {
    return "You each have something the other wants";
  }
  if (direction === "they_want") {
    // Tradable cards live in the Trade Binder, not the Vault (prized,
    // not-for-trade). Named consistently with the reveal and the callout (#7A).
    return "They want something from your Trade Binder";
  }
  return "They have something on your wishlist";
}

/** Total cards involved in a match, both directions. */
export function matchCardCount(trader) {
  if (!trader) return 0;
  return (
    (trader.matchedCards || []).length + (trader.theyWantCards || []).length
  );
}

export function buildRevealViewport(user, currentViewport = {}) {
  const coords = getUserLngLat(user);
  if (!coords) return null;
  return {
    ...currentViewport,
    longitude: coords.longitude,
    latitude: coords.latitude,
    zoom: Math.max(currentViewport.zoom || 12, 15),
    transitionDuration: 1000,
  };
}

/** Mystery slot count for reveal sheet teasers (no card art). Counts whichever
 * direction the match runs so a reverse (they_want) match still shows slots (#7A). */
export function mysterySlotCount(user, max = 3) {
  const has = Array.isArray(user?.matchedCards) ? user.matchedCards.length : 0;
  const wants = Array.isArray(user?.theyWantCards) ? user.theyWantCards.length : 0;
  const n = Math.max(has, wants);
  if (n <= 0) return 1;
  return Math.min(n, max);
}
